import { Parser } from 'node-sql-parser';

// Create a parser instance
const parser = new Parser();

// Prevent SQL injection
const DANGEROUS_OPERATORS = [';', '--', '/*', '*/'];

/**
 * Validates SQL WHERE clause or filter conditions using a proper SQL parser
 * @param {string} sql - The SQL to validate
 * @param {Object} options - Optional configuration
 * @param {string} options.columnName - Column name that must be present in the SQL
 * @param {boolean} options.requireColumnName - Whether to enforce column name presence
 * @returns {Object} Validation result containing isValid, error message, and errorType
 */
export const validateSQL = (sql, options = {}) => {
  const { columnName, requireColumnName = false } = options;

  // Check for empty SQL
  if (!sql || sql.trim() === '') {
    return {
      isValid: false,
      error: 'SQL query cannot be empty',
      errorType: 'empty'
    };
  }

  // Check for dangerous operators that might enable SQL injection
  const containsDangerousOperators = DANGEROUS_OPERATORS.some(op => sql.includes(op));
  if (containsDangerousOperators) {
    return {
      isValid: false,
      error: `SQL contains disallowed operators: ${DANGEROUS_OPERATORS.join(', ')}`,
      errorType: 'disallowedOperator'
    };
  }

  try {
    // Wrap the condition in a valid query for parsing
    const wrappedSQL = `SELECT * FROM dummy_table WHERE ${sql}`;
    
    // Parse the SQL to validate its syntax
    parser.astify(wrappedSQL);
    
    // If we get here, the SQL is syntactically valid
    
    // Check for required column name if specified
    if (requireColumnName && columnName) {
      // Get all column references directly from the parser
      const columnRefs = parser.columnList(wrappedSQL);
      
      // Column references will be in format "select::table::column"
      const columnPresent = columnRefs.some(colRef => {
        const parts = colRef.split('::');
        const col = parts[parts.length - 1]; // Last part is the column name
        return col === columnName || col.endsWith(`.${columnName}`);
      });
      
      if (!columnPresent) {
        return {
          isValid: false,
          error: `Filter must include the column name (${columnName})`,
          errorType: 'missingColumn'
        };
      }
    }

    return { isValid: true, errorType: null };
  } catch (error) {
    return {
      isValid: false,
      error: `Invalid SQL: ${error.message}`,
      errorType: 'syntaxError'
    };
  }
};

// Export the list of dangerous operators for reference
export const DANGEROUS_OPERATORS_LIST = DANGEROUS_OPERATORS; 